import { useCallback, useEffect, useRef, useState } from "react";
import remindersApi from "../services/reminders.api.js";
import { playReminderChime, primeAudio } from "../utils/feedback.js";

/**
 * useAppointmentReminders — the browser half of the reminder pipeline.
 *
 * Free-tier backends (Render) can't run a dependable always-on cron, so the
 * durable trigger lives in MongoDB and this hook does the delivery: while the
 * dashboard/PWA is open it polls `/api/reminders/due` every 60s and, for each
 * reminder whose time has passed, raises BOTH delivery channels:
 *
 *   1. the in-app glowing banner (permission-free — it renders inside the app
 *      shell, so it still shows when the browser's native Notification
 *      permission is denied, blocked, or the tab/window is in the background);
 *   2. the native OS notification, when the browser allows it.
 *
 * A gentle Web Audio chime accompanies every alert, then the reminder is marked
 * `sent` server-side immediately, so a reload, a retry or a second tab can
 * never ring the same reminder twice.
 *
 * Every cycle of the loop logs with a `[Reminder Poller]` prefix, so the poll
 * cadence, the backend response and each `PUT /api/reminders/:id/status` write
 * can be verified straight from the browser console.
 *
 * Cadence is intentionally gentle: 60s while the tab is visible, backing off to
 * 5 minutes while it is hidden (a minimised counter screen still catches its
 * reminders without hammering the free-tier request budget), plus an immediate
 * check whenever the tab regains focus.
 */
export const REMINDER_POLL_MS = 60 * 1000;
/** Slower cadence while the tab is hidden — still delivers, at ~1/5 the cost. */
export const REMINDER_HIDDEN_POLL_MS = 5 * 60 * 1000;
export const REMINDER_NOTIFICATION_TITLE = "Upcoming Appointment!";

/** Tag prefix for the sessionStorage set — see `loadNotified`. */
const NOTIFIED_KEY = "swastik-reminders-notified";

/**
 * Hard cap on banner cards rendered at once. A backend that slept for hours
 * can hand back a burst (the API caps at 50); the drawer stays a screen-sized
 * alert instead of covering the app, and every suppressed reminder still has
 * its console line plus the native notification when that is permitted.
 */
const MAX_VISIBLE_ALERTS = 5;

/* -------------------------------------------------------------------------- */
/* Audit logging                                                              */
/* -------------------------------------------------------------------------- */

/** ISO timestamp so console lines sort and are unambiguous across timezones. */
const stamp = () => new Date().toISOString();

const logPoll = (message) => console.log(`[Reminder Poller] ${message}`);

const logPollError = (message, error) =>
  console.error(`[Reminder Poller] ${message}`, error?.message ?? error);

/* -------------------------------------------------------------------------- */
/* Reminder presentation helpers (shared with the in-app banner)              */
/* -------------------------------------------------------------------------- */

export const isNotificationSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

const readPermission = () =>
  isNotificationSupported() ? Notification.permission : "unsupported";

/** Local calendar day as `YYYY-MM-DD` — string-comparable, no UTC surprises. */
const toDateKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

/** Appointment timestamp -> "3:30 PM" for notifications and banners. */
const formatReminderTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

/** Appointment timestamp -> "Today" / "Tomorrow" / "Fri, 16 Oct". */
const formatReminderDay = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  const dayDiff = Math.round(
    (new Date(date.getFullYear(), date.getMonth(), date.getDate()) -
      new Date(today.getFullYear(), today.getMonth(), today.getDate())) /
      86_400_000,
  );
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Tomorrow";
  if (dayDiff === -1) return "Yesterday";
  return new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
};

/**
 * Which appointments tab a booking belongs on (`today` / `upcoming` / `past`),
 * so the banner's "View appointment" lands on the schedule that lists it.
 */
export const appointmentTabFor = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "today";
  const key = toDateKey(date);
  const todayKey = toDateKey(new Date());
  if (key === todayKey) return "today";
  return key > todayKey ? "upcoming" : "past";
};

export const buildReminderBody = (reminder) => {
  const customerName = reminder?.customerName ?? "A client";
  const serviceName = reminder?.serviceName ?? "salon";
  const time = formatReminderTime(reminder?.appointmentTime);
  return `${customerName} has a ${serviceName} appointment at ${time}. Please prepare.`;
};

/**
 * Normalised shape the banner renders: everything the drawer needs (name,
 * service, a pre-formatted "Today · 3:30 PM" line and the target tab) is
 * computed once here, so the component stays presentational.
 */
const toAlert = (reminder, kind = "appointment") => {
  const appointmentTime = reminder?.appointmentTime ?? new Date().toISOString();
  const dayLabel = formatReminderDay(appointmentTime);
  const timeLabel = formatReminderTime(appointmentTime);
  return {
    id: String(reminder?.id ?? reminder?._id ?? ""),
    kind,
    customerName: reminder?.customerName ?? (kind === "test" ? "Test reminder" : "A client"),
    serviceName: reminder?.serviceName ?? "salon",
    appointmentTime,
    whenLabel: [dayLabel, timeLabel].filter(Boolean).join(" · "),
    tab: appointmentTabFor(appointmentTime),
  };
};

/**
 * Ids already alerted in this tab session. If the "mark sent" write is lost
 * (offline, rate-limited, backend asleep) the reminder stays pending and comes
 * back on the next poll — this set is what stops that retry from ringing a
 * second time. Persisted so a mid-session reload can't re-fire either.
 */
const loadNotified = () => {
  try {
    const raw = window.sessionStorage.getItem(NOTIFIED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const persistNotified = (set) => {
  try {
    window.sessionStorage.setItem(NOTIFIED_KEY, JSON.stringify([...set]));
  } catch {
    /* private mode / storage full — in-memory set still guards this session */
  }
};

export default function useAppointmentReminders({ enabled = true } = {}) {
  const [permission, setPermission] = useState(readPermission);
  const [lastError, setLastError] = useState(null);
  /** Cards in the in-app drawer — newest first, capped at MAX_VISIBLE_ALERTS. */
  const [alerts, setAlerts] = useState([]);
  // Guards against a reminder firing twice while its "sent" write is in flight.
  const inFlight = useRef(new Set());
  // Lazy initialiser: storage is read once per mount, not every render.
  const [notified] = useState(loadNotified);

  /**
   * Puts a card at the top of the in-app drawer. Idempotent per id, so a
   * re-render or a repeated dispatch can never stack duplicates.
   */
  const pushAlert = useCallback((alert) => {
    if (!alert?.id) return;
    setAlerts((prev) => {
      if (prev.some((item) => item.id === alert.id)) return prev;
      const next = [alert, ...prev];
      if (next.length > MAX_VISIBLE_ALERTS) {
        console.warn(
          `[Reminder Poller] Drawer at its ${MAX_VISIBLE_ALERTS}-card cap at ${stamp()} — dropped the oldest card (${next[next.length - 1]?.id}) from the screen; it keeps its console line and native notification.`,
        );
        return next.slice(0, MAX_VISIBLE_ALERTS);
      }
      return next;
    });
  }, []);

  const dismissAlert = useCallback((id) => {
    const key = String(id ?? "");
    setAlerts((prev) => prev.filter((item) => item.id !== key));
    logPoll(`Banner for ${key || "unknown reminder"} dismissed by staff at ${stamp()}`);
  }, []);

  // Only reachable from the drawer, which renders nothing while empty — so the
  // log is safe to write unconditionally and outside the state updater.
  const dismissAllAlerts = useCallback(() => {
    setAlerts([]);
    logPoll(`All banner cards dismissed by staff at ${stamp()}`);
  }, []);

  /**
   * Deliver one due reminder: native notification (best effort) + in-app
   * banner + chime (always), then persist `sent` so the next poll can't
   * duplicate it.
   */
  const dispatch = useCallback(
    async (reminder) => {
      const id = String(reminder?.id ?? reminder?._id ?? "");
      if (!id || inFlight.current.has(id)) return;

      inFlight.current.add(id);
      try {
        // Already alerted in this session: only reconcile the server record,
        // never raise a second notification.
        if (notified.has(id)) {
          try {
            await remindersApi.updateStatus(id, "sent");
            logPoll(
              `Reminder ${id} already alerted this session — PUT /api/reminders/${id}/status → sent re-confirmed at ${stamp()}`,
            );
          } catch (error) {
            logPollError(
              `Reminder ${id} status reconcile failed at ${stamp()} — it stays pending and is retried next poll without re-alerting.`,
              error,
            );
          }
          return;
        }

        // 1. Native OS notification — best effort, only when permitted. A
        //    constructor throw or a denied permission must never block the
        //    in-app delivery below.
        let native = false;
        if (isNotificationSupported() && Notification.permission === "granted") {
          try {
            const notification = new Notification(REMINDER_NOTIFICATION_TITLE, {
              body: buildReminderBody(reminder),
              icon: "/favicon.svg",
              badge: "/favicon.svg",
              tag: `appointment-reminder-${id}`,
            });
            notification.onclick = () => {
              try {
                window.focus();
              } catch {
                /* focus may be blocked; the alert itself already fired */
              }
              notification.close?.();
            };
            native = true;
          } catch (error) {
            logPollError(
              `Native notification for reminder ${id} failed at ${stamp()} — the in-app banner still covers it.`,
              error,
            );
          }
        }

        // 2. In-app banner + chime — ALWAYS, independent of Notification
        //    permission, so a blocked/minimised browser still alerts staff.
        pushAlert(toAlert(reminder));
        playReminderChime();

        logPoll(
          `Raised reminder ${id} (${reminder?.customerName ?? "client"} / ${
            reminder?.serviceName ?? "salon"
          }) at ${stamp()} — in-app banner shown, chime played, native notification ${
            native ? "also shown" : `not available (permission: ${readPermission()})`
          }`,
        );

        // 3. Record locally the instant the alert is on screen, then mark it
        //    sent server-side so a retry can never duplicate the alert.
        notified.add(id);
        persistNotified(notified);
      } catch (error) {
        // Delivery itself blew up (banner/chime/Notification all failed): the
        // reminder was never seen by staff, so record it as failed rather than
        // letting it loop forever unattended.
        setLastError(error);
        logPollError(`Delivering reminder ${id} failed at ${stamp()}.`, error);
        try {
          await remindersApi.updateStatus(id, "failed");
          logPoll(`PUT /api/reminders/${id}/status → failed at ${stamp()}`);
        } catch (statusError) {
          logPollError(
            `PUT /api/reminders/${id}/status could not record "failed" at ${stamp()} — left pending; the next poll retries without re-alerting.`,
            statusError,
          );
        }
        return;
      } finally {
        inFlight.current.delete(id);
      }

      // 4. Delivery succeeded — persist `sent`. If this write is lost the
      //    reminder stays `pending`, comes back on the next poll, hits the
      //    `notified` guard above and only retries the status write: the
      //    banner never fires twice.
      try {
        await remindersApi.updateStatus(id, "sent");
        logPoll(
          `PUT /api/reminders/${id}/status → sent at ${stamp()} — reminder closed, no duplicate loop`,
        );
      } catch (error) {
        setLastError(error);
        logPollError(
          `PUT /api/reminders/${id}/status → "sent" write LOST at ${stamp()} — left pending; the next poll retries the write only (banner is already up, no re-alert).`,
          error,
        );
      }
    },
    [notified, pushAlert],
  );

  const check = useCallback(async () => {
    if (!enabled) return;

    let data;
    try {
      data = await remindersApi.getDue();
    } catch (error) {
      logPollError(
        `Checked due reminders at ${stamp()} — GET /api/reminders/due FAILED.`,
        error,
      );
      setLastError(error);
      return;
    }

    const reminders = data?.reminders ?? [];
    const now = Date.now();
    logPoll(
      `Checked due reminders at ${stamp()} — GET /api/reminders/due OK, ${reminders.length} due (tab ${document.visibilityState})`,
    );

    if (reminders.length) {
      // One entry per due reminder so F12 shows exactly what the backend
      // returned and how each trigger compares to this browser's clock. The
      // backend already filtered on `reminderTime <= now`, so every row should
      // read `due: true` — a `false` here would flag a clock/zone mismatch.
      console.log(
        `[Reminder Poller] ${reminders.length} due reminder(s) to trigger:`,
        reminders.map((reminder) => {
          const id = String(reminder?.id ?? reminder?._id ?? "");
          const trigger = new Date(reminder?.reminderTime).getTime();
          return {
            id,
            status: reminder?.status,
            reminderTime: reminder?.reminderTime,
            appointmentTime: reminder?.appointmentTime,
            triggerLocal: Number.isFinite(trigger)
              ? new Date(trigger).toLocaleString()
              : "invalid",
            due: Number.isFinite(trigger) ? trigger <= now : false,
          };
        }),
      );
    }

    // Sequential: a burst of due reminders should queue, not stampede.
    for (const reminder of reminders) {
      const id = String(reminder?.id ?? reminder?._id ?? "");
      const nativeReady =
        isNotificationSupported() && Notification.permission === "granted";
      logPoll(
        `Triggering reminder ${id} at ${stamp()} — raising in-app banner + chime${
          nativeReady ? " + native notification" : ""
        }`,
      );
      // eslint-disable-next-line no-await-in-loop
      await dispatch(reminder);
    }
  }, [enabled, dispatch]);

  useEffect(() => {
    if (!enabled) return undefined;

    // Browsers gate Web Audio behind a user gesture: unlock the shared
    // AudioContext on the first pointer/key event so a later timer-driven
    // reminder chime is not silently swallowed.
    const unlockAudio = () => {
      primeAudio();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    let timer = 0;

    // Visible tabs poll on the 60s cadence; hidden ones back off to 5 minutes
    // so a minimised counter screen still catches its reminders (native pop-ups
    // included) without the full request rate.
    const cadence = () =>
      document.visibilityState === "visible"
        ? REMINDER_POLL_MS
        : REMINDER_HIDDEN_POLL_MS;

    const tick = () => {
      check();
      timer = window.setTimeout(tick, cadence());
    };

    // In dev StrictMode this effect mounts twice, so a start/stop/start triple
    // in the console is normal there — production logs a single start.
    logPoll(
      `Loop started — GET /api/reminders/due every ${
        REMINDER_POLL_MS / 1000
      }s (${REMINDER_HIDDEN_POLL_MS / 60000} min while hidden), checking now`,
    );
    timer = window.setTimeout(tick, 0);

    // Focus changes re-check immediately (and re-arm on the new cadence)
    // instead of waiting out the tick.
    const onVisibility = () => {
      window.clearTimeout(timer);
      check();
      timer = window.setTimeout(tick, cadence());
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      logPoll(`Loop stopped at ${stamp()}`);
    };
  }, [enabled, check]);

  const requestPermission = useCallback(async () => {
    if (!isNotificationSupported()) return "unsupported";
    let result;
    try {
      result = await Notification.requestPermission();
    } catch {
      result = readPermission();
    }
    setPermission(result);
    logPoll(`Native notification permission now "${result}" at ${stamp()}`);
    // Deliver anything already overdue right away instead of waiting a tick.
    // The in-app banner works either way, so only a grant needs the catch-up.
    if (result === "granted") check();
    return result;
  }, [check]);

  const sendTestNotification = useCallback(async () => {
    // The in-app banner + chime need no browser permission, so the test always
    // exercises them — that is the channel that survives a denied/blocked
    // browser. The native notification is the optional extra on top.
    pushAlert(
      toAlert(
        {
          id: `test-${Date.now()}`,
          customerName: "Test reminder",
          serviceName: "In-app banner & chime check",
          appointmentTime: new Date().toISOString(),
        },
        "test",
      ),
    );
    playReminderChime();
    logPoll(
      `Test alert raised at ${stamp()} — in-app banner shown + chime played (native permission: ${readPermission()})`,
    );

    if (!isNotificationSupported()) return "unsupported";

    let result = Notification.permission;
    if (result === "default") result = await requestPermission();
    if (result !== "granted") return result;

    try {
      new Notification("Test Notification", {
        body: "Appointment reminders are working. You'll be alerted before each booking.",
        icon: "/favicon.svg",
        badge: "/favicon.svg",
        tag: "appointment-reminder-test",
      });
    } catch (error) {
      logPollError(`Test native notification failed at ${stamp()}.`, error);
    }
    return "granted";
  }, [requestPermission, pushAlert]);

  return {
    permission,
    supported: isNotificationSupported(),
    requestPermission,
    sendTestNotification,
    check,
    lastError,
    // In-app drawer state — rendered by <ReminderBanner /> in AppLayout.
    alerts,
    dismissAlert,
    dismissAllAlerts,
  };
}
